import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {
  Dimensions,
  FlatList,
  SafeAreaView,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import Icon from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import {retrieveSetting} from '../Common/common';
import SettlementDialog from '../Components/SettlementDialog';
import {useGetModesQuery} from '../redux/services/amodeApi';
import {useGetPOMTablesQuery} from '../redux/services/pomtableApi';
import {useInsertPOEBillSettleMutation} from '../redux/services/poebillsettleApi';
import {RootState} from '../redux/store';
import {
  colors,
  spacing,
  shadows,
  borderRadius,
  typography,
} from '../theme/theme';

import T from '../Components/T';
const POSTables = ({navigation}: UniversalProps) => {
  const [numColumns, setNumColumns] = useState(2);
  const [key, setKey] = useState('flatlist_key_2');
  const [skip, setSkip] = useState(true);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get('window').width,
  );
  const gdata = useSelector((state: RootState) => state.gdata);
  const [defOutlet, setDefOutlet] = useState(13);
  const [defArea, setDefArea] = useState('');
  const [isSettlementDialogOpen, setIsSettlementDialogOpen] = useState(false);
  const [selectedBilledTable, setSelectedBilledTable] = useState<any>(null);

  const {data: modeData} = useGetModesQuery(gdata.company);
  const [insertPOEBillSettle] = useInsertPOEBillSettleMutation();

  useEffect(() => {
    const retDefOutlet = async () => {
      const val = Number(await retrieveSetting('defoutlet'));
      setDefOutlet(val);
    };
    retDefOutlet();
  }, []);

  useEffect(() => {
    const retDefArea = async () => {
      setDefArea(await retrieveSetting('defarea'));
    };
    retDefArea();
  }, []);

  useEffect(() => {
    const updateLayout = () => {
      const {width} = Dimensions.get('window');
      setScreenWidth(width);

      const isTablet = width >= 768;
      const isLargeMobile = width >= 400;

      if (isTablet) {
        setNumColumns(5);
        setKey(`flatlist_key_5`);
      } else if (isLargeMobile) {
        setNumColumns(3);
        setKey(`flatlist_key_3`);
      } else {
        setNumColumns(2);
        setKey(`flatlist_key_2`);
      }
    };

    updateLayout();
    const subscription = Dimensions.addEventListener('change', updateLayout);
    return () => subscription?.remove();
  }, []);

  const {data} = useGetPOMTablesQuery(
    {cid: gdata.company, olid: defOutlet},
    {skip: !defOutlet},
  );

  const [sortMode, setSortMode] = useState<'sort' | 'name'>('sort');
  const [groupTables, setGroupTables] = useState(true);

  const sortedData = Array.isArray(data)
    ? [...data].sort((a, b) =>
        sortMode === 'name'
          ? String(a.tb_name).localeCompare(String(b.tb_name), undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          : (a.tb_sort || 0) - (b.tb_sort || 0) ||
            String(a.tb_name).localeCompare(String(b.tb_name), undefined, {
              numeric: true,
              sensitivity: 'base',
            }),
      )
    : [];

  const tables = sortedData
    .filter(v => v.tb_areaname === defArea)
    .map(value => ({
      tb_name: value.tb_name,
      tb_date: moment(value.tb_date).format('DD-MM-YY hh:mm'),
      tb_waiter: value.tb_waiter,
      tb_id: value.tb_id,
      tb_status: value.tb_status,
      tb_capcity: value.tb_capcity,
      tb_waitername: value.tb_waitername,
      bl_id: value.bl_id,
      value,
    }));

  // Group tables by tb_groupname, ordered by the group's sort number
  // (sortedData already orders tables within each group)
  const sections = (() => {
    const map = new Map<string, {name: string; sort: number; items: any[]}>();
    tables.forEach(t => {
      const name = t.value.tb_groupname || 'Ungrouped';
      const sec = map.get(name) ?? {
        name,
        sort: Number(t.value.tb_groupsort ?? 9999),
        items: [],
      };
      sec.items.push(t);
      map.set(name, sec);
    });
    const list = Array.from(map.values()).sort(
      (a, b) =>
        a.sort - b.sort ||
        a.name.localeCompare(b.name, undefined, {numeric: true}),
    );
    // One group (nothing configured) or grouping off: a single list, no heading
    if (!groupTables || list.length <= 1) {
      return [{name: '', sort: 0, items: tables}];
    }
    return list;
  })();

  const getTableStatus = (item: any) => {
    if (item.bl_id == null) {
      return item.tb_status === null ? 'available' : 'occupied';
    }
    return 'reserved';
  };

  const getStatusConfig = (item: any) => {
    const status = getTableStatus(item);
    switch (status) {
      case 'available':
        return {
          backgroundColor: colors.successLight,
          borderColor: colors.success,
          icon: 'check-circle',
          text: 'Available',
          textColor: colors.success,
        };
      case 'occupied':
        return {
          backgroundColor: colors.errorLight,
          borderColor: colors.error,
          icon: 'times-circle',
          text: 'Occupied',
          textColor: colors.error,
        };
      case 'reserved':
        return {
          backgroundColor: colors.infoLight,
          borderColor: colors.info,
          icon: 'clock-o',
          text: 'Reserved',
          textColor: colors.info,
        };
      default:
        return {
          backgroundColor: colors.backgroundSecondary,
          borderColor: colors.borderMedium,
          icon: 'question-circle',
          text: 'Unknown',
          textColor: colors.textSecondary,
        };
    }
  };

  const handleAction = (action: string, item: any, event: any) => {
    event.stopPropagation();

    switch (action) {
      case 'change-table':
        navigation.navigate('ChangeTable', {table: item.value});
        break;
      case 'add-order':
        navigation.navigate('Products', {table: item.value});
        break;
      case 'cancel-order':
        navigation.navigate('CancelKOT', {table: item.value});
        break;
    }
  };

  const renderItem = ({item}: {item: any}) => {
    const statusConfig = getStatusConfig(item);
    const isOccupied = item.bl_id == null && item.tb_status !== null;

    const cardWidth = screenWidth / numColumns - spacing.base - spacing.xs;

    return (
      <TouchableOpacity
        style={[screenStyles.tableCard, {width: cardWidth}]}
        onPress={() => {
          if (item.bl_id == null) {
            // Not reserved - can be available or occupied
            if (item.tb_status === null) {
              // Available - go to products to start new KOT
              navigation.navigate('Products', {table: item.value});
            } else {
              // Occupied - go to bill
              navigation.navigate('Bill', {table: item.value});
            }
          } else {
            // Reserved (billed) - open settlement dialog
            setSelectedBilledTable(item.value);
            setIsSettlementDialogOpen(true);
          }
        }}
        disabled={false}>
        <View
          style={[
            screenStyles.statusIndicator,
            {backgroundColor: statusConfig.borderColor},
          ]}
        />
        <View style={screenStyles.tableContent}>
          <View
            style={[
              screenStyles.tableNumberCircle,
              {borderColor: statusConfig.borderColor},
            ]}>
            <Text
              style={[
                screenStyles.tableNumber,
                {color: statusConfig.textColor},
              ]}>
              {item.tb_name}
            </Text>
          </View>
          <View style={screenStyles.tableInfo}>
            <View style={screenStyles.statusBadge}>
              <Icon
                name={statusConfig.icon}
                size={12}
                color={statusConfig.textColor}
              />
              <Text
                style={[
                  screenStyles.statusLabel,
                  {color: statusConfig.textColor},
                ]}>
                {statusConfig.text}
              </Text>
            </View>
            <View style={screenStyles.tableDetails}>
              <Icon name="users" size={14} color={colors.textSecondary} />
              <Text style={screenStyles.detailText}>{item.tb_capcity} <T>Pax</T></Text>
            </View>
            {item.tb_waitername && (
              <View style={screenStyles.tableDetails}>
                <Icon name="user" size={14} color={colors.textSecondary} />
                <Text style={screenStyles.detailText} numberOfLines={1}>
                  {item.tb_waitername}
                </Text>
              </View>
            )}
            {item.tb_date && item.bl_id == null && item.tb_status !== null && (
              <View style={screenStyles.tableDetails}>
                <Icon name="clock-o" size={14} color={colors.textSecondary} />
                <Text style={screenStyles.detailText}>{item.tb_date}</Text>
              </View>
            )}
          </View>

          {/* Action buttons for occupied tables */}
          {isOccupied && (
            <View style={screenStyles.actionButtons}>
              <TouchableOpacity
                style={[
                  screenStyles.actionButton,
                  {backgroundColor: colors.info},
                ]}
                activeOpacity={0.7}
                onPress={e => handleAction('add-order', item, e)}>
                <Icon name="plus-circle" size={20} color={colors.white} />
                <Text style={screenStyles.actionButtonText}><T>Order</T></Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  screenStyles.actionButton,
                  {backgroundColor: colors.warning},
                ]}
                activeOpacity={0.7}
                onPress={e => handleAction('change-table', item, e)}>
                <Icon name="exchange" size={20} color={colors.white} />
                <Text style={screenStyles.actionButtonText}><T>Move</T></Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  screenStyles.actionButton,
                  {backgroundColor: colors.error},
                ]}
                activeOpacity={0.7}
                onPress={e => handleAction('cancel-order', item, e)}>
                <Icon name="times-circle" size={20} color={colors.white} />
                <Text style={screenStyles.actionButtonText}><T>Cancel</T></Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={screenStyles.container}>
      <View style={screenStyles.header}>
        <Text style={screenStyles.headerTitle}><T>Tables</T></Text>
        <Text style={screenStyles.headerSubtitle}>
          {tables.length} <T>table</T>{tables.length !== 1 ? 's' : ''} <T>in area</T>
        </Text>
        <View style={screenStyles.toolbar}>
          {(['sort', 'name'] as const).map(m => (
            <TouchableOpacity
              key={m}
              style={[
                screenStyles.chip,
                sortMode === m && screenStyles.chipActive,
              ]}
              onPress={() => setSortMode(m)}>
              <Text
                style={[
                  screenStyles.chipText,
                  sortMode === m && screenStyles.chipTextActive,
                ]}>
                {m === 'sort' ? 'Table order' : 'Table name'}
              </Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={[
              screenStyles.chip,
              groupTables && screenStyles.chipActive,
            ]}
            onPress={() => setGroupTables(g => !g)}>
            <Text
              style={[
                screenStyles.chipText,
                groupTables && screenStyles.chipTextActive,
              ]}>
              <T>Group</T>
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {tables.length === 0 ? (
        <View style={screenStyles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={screenStyles.loadingText}><T>Loading Tables...</T></Text>
        </View>
      ) : (
        <FlatList
          key={key}
          data={sections}
          renderItem={({item: section}) => (
            <View>
              {section.name !== '' && (
                <Text style={screenStyles.groupHeader}>
                  {section.name}{' '}
                  <Text style={screenStyles.groupCount}>
                    (
                    {
                      section.items.filter(
                        (t: any) => t.tb_status !== null && t.bl_id == null,
                      ).length
                    }
                    /{section.items.length} <T>occupied)</T>
                  </Text>
                </Text>
              )}
              <View style={screenStyles.groupGrid}>
                {section.items.map((t: any, i: number) => (
                  <React.Fragment key={`${t.value.tb_checkin}-${t.tb_id}-${i}`}>
                    {renderItem({item: t})}
                  </React.Fragment>
                ))}
              </View>
            </View>
          )}
          keyExtractor={(section, index) => `${section.name}-${index}`}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={screenStyles.listContent}
          showsVerticalScrollIndicator={false}
        />
      )}

      {/* Settlement Dialog */}
      {selectedBilledTable && (
        <SettlementDialog
          visible={isSettlementDialogOpen}
          onClose={() => {
            setIsSettlementDialogOpen(false);
            setSelectedBilledTable(null);
          }}
          tableData={selectedBilledTable}
          modeData={modeData || []}
          billAmount={
            selectedBilledTable?.bl_amt ? Number(selectedBilledTable.bl_amt) : 0
          }
          billId={
            selectedBilledTable?.bl_id ? Number(selectedBilledTable.bl_id) : 0
          }
          onSettle={async settlementData => {
            try {
              await insertPOEBillSettle(settlementData).unwrap();
            } catch (error) {
              throw error;
            }
          }}
        />
      )}
    </View>
  );
};

const screenStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.backgroundTertiary,
  },
  safeArea: {
    flex: 1,
    paddingHorizontal: spacing.base,
    paddingTop: spacing.sm,
  },
  header: {
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.base,
    backgroundColor: colors.white,
    ...shadows.sm,
    marginBottom: spacing.base,
    borderRadius: borderRadius.lg,
  },
  headerTitle: {
    fontSize: typography.fontSize['3xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    marginBottom: spacing.xs,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textTertiary,
    fontWeight: typography.fontWeight.regular,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.base,
    fontSize: typography.fontSize.base,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium,
  },
  listContent: {
    paddingVertical: spacing.md,
    paddingBottom: 80,
    gap: spacing.base,
  },
  groupHeader: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  groupCount: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.regular,
    color: colors.textTertiary,
  },
  groupGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  toolbar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  chip: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.full,
    borderWidth: 1,
    borderColor: colors.borderMedium,
  },
  chipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  chipText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  chipTextActive: {
    color: colors.white,
  },
  tableCard: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.lg,
    ...shadows.md,
    borderWidth: 2,
    borderColor: 'transparent',
    overflow: 'hidden',
    minHeight: 160,
    alignSelf: 'flex-start',
    marginBottom: spacing.sm,
  },
  statusIndicator: {
    height: 4,
    width: '100%',
  },
  tableContent: {
    padding: spacing.base,
    flex: 1,
    justifyContent: 'space-between',
  },
  tableNumberCircle: {
    width: 50,
    height: 50,
    borderRadius: borderRadius.full,
    borderWidth: 3,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.sm,
    alignSelf: 'center',
    backgroundColor: colors.white,
  },
  tableNumber: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  tableInfo: {
    marginTop: spacing.xs,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.base,
    alignSelf: 'flex-start',
  },
  statusLabel: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    marginLeft: spacing.xs,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  detailText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginLeft: spacing.xs,
    flex: 1,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
    gap: spacing.sm,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderRadius: borderRadius.base,
    gap: 4,
    minHeight: 50,
  },
  actionButtonText: {
    fontSize: 10,
    color: colors.white,
    fontWeight: typography.fontWeight.semibold,
    marginTop: 2,
    textAlign: 'center',
  },
});

export default POSTables;

